import React, { useState } from 'react';
import { X, Home, DollarSign, Square, Bed, Bath, Car, Clock, Building, TreePine, Camera, Users, MapIcon, Gauge, GraduationCap, User, FileText, Shield, Eye, EyeOff } from 'lucide-react';
import PropertyImageGallery from './PropertyImageGallery';

const PropertyDetailsModal = ({ property, isOpen, onClose }) => {
  const [showFullDetails, setShowFullDetails] = useState(false);

  if (!isOpen || !property) return null;

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl max-h-[90vh] w-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Home className="w-6 h-6 text-blue-400" />
              Property Details
            </h2>
            <p className="text-gray-300 mt-1">
              {property.UnparsedAddress || property.Address || 'Property Address'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Property Images */}
          {property.ImageURLs && property.ImageURLs.length > 0 && (
            <PropertyImageGallery 
              images={property.ImageURLs} 
              propertyAddress={property.UnparsedAddress || property.Address || 'Property'} 
            />
          )}

          {/* Basic Key Property Stats - Always Shown */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{property.BedroomsTotal || 'N/A'}</div>
              <div className="text-slate-300 text-sm flex items-center justify-center gap-1">
                <Bed className="w-4 h-4" />
                Bedrooms
              </div>
            </div>
            <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">
                {property.BathroomsDecimal || property.BathroomsTotalInteger || property.BathroomsFull || 'N/A'}
              </div>
              <div className="text-slate-300 text-sm flex items-center justify-center gap-1">
                <Bath className="w-4 h-4" />
                Bathrooms
              </div>
            </div>
            <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">
                {property.LivingArea ? `${parseInt(property.LivingArea).toLocaleString()}` : 'N/A'}
              </div>
              <div className="text-slate-300 text-sm flex items-center justify-center gap-1">
                <Square className="w-4 h-4" />
                Sq Ft
              </div>
            </div>
            <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{property.YearBuilt || 'N/A'}</div>
              <div className="text-slate-300 text-sm">Year Built</div>
            </div>
            <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">
                {property.StoriesTotal || property.DerivedStories || 'N/A'}
              </div>
              <div className="text-slate-300 text-sm">Stories</div>
            </div>
            <div className="bg-slate-700 border border-slate-600 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{property.RoomsTotal || 'N/A'}</div>
              <div className="text-slate-300 text-sm">Total Rooms</div>
            </div>
          </div>

          {/* Basic Pricing Information - Always Shown */}
          <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-blue-300 mb-6 flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              Pricing Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {property.ListPrice && (
                <div className="bg-green-600 p-4 rounded-lg text-center">
                  <div className="text-xl font-bold text-white">
                    {formatCurrency(property.ListPrice)}
                  </div>
                  <div className="text-green-100 text-sm">List Price</div>
                </div>
              )}
              {property.ClosePrice && (
                <div className="bg-blue-600 p-4 rounded-lg text-center">
                  <div className="text-xl font-bold text-white">
                    {formatCurrency(property.ClosePrice)}
                  </div>
                  <div className="text-blue-100 text-sm">Sale Price</div>
                </div>
              )}
              {property.PriceperSquareFeet && (
                <div className="bg-purple-600 p-4 rounded-lg text-center">
                  <div className="text-xl font-bold text-white">${property.PriceperSquareFeet}</div>
                  <div className="text-purple-100 text-sm">Per Sq Ft</div>
                </div>
              )}
              {(property.PublicRecord?.AssessedValue) && (
                <div className="bg-orange-600 p-4 rounded-lg text-center">
                  <div className="text-xl font-bold text-white">
                    {formatCurrency(property.PublicRecord.AssessedValue)}
                  </div>
                  <div className="text-orange-100 text-sm">Assessed Value</div>
                </div>
              )}
            </div>
          </div>

          {/* Basic Property Description - Always Shown */}
          {property.PublicRemarks && (
            <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-blue-300 mb-4">Property Description</h4>
              <div className="bg-slate-600 p-4 rounded-lg">
                <p className="text-slate-200 text-sm leading-relaxed">
                  {property.PublicRemarks}
                </p>
              </div>
            </div>
          )}

          {/* Basic Property Features - Always Shown */}
          <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-blue-300 mb-4">Key Features</h4>
            <div className="flex flex-wrap gap-2">
              {property.BedroomsTotal && (
                <span className="px-3 py-1 bg-blue-700 text-blue-100 text-sm rounded-full">
                  {property.BedroomsTotal} Bedrooms
                </span>
              )}
              {property.BathroomsDecimal && (
                <span className="px-3 py-1 bg-blue-700 text-blue-100 text-sm rounded-full">
                  {property.BathroomsDecimal} Bathrooms
                </span>
              )}
              {property.LivingArea && (
                <span className="px-3 py-1 bg-purple-700 text-purple-100 text-sm rounded-full">
                  {parseInt(property.LivingArea).toLocaleString()} sq ft
                </span>
              )}
              {property.YearBuilt && (
                <span className="px-3 py-1 bg-indigo-700 text-indigo-100 text-sm rounded-full">
                  Built {property.YearBuilt}
                </span>
              )}
              {property.GarageYN === 'Y' && (
                <span className="px-3 py-1 bg-gray-700 text-gray-100 text-sm rounded-full">
                  Garage
                </span>
              )}
              {property.FireplaceYN === 'Y' && (
                <span className="px-3 py-1 bg-red-700 text-red-100 text-sm rounded-full">
                  Fireplace
                </span>
              )}
              {property.PoolFeatures && property.PoolFeatures !== '' && (
                <span className="px-3 py-1 bg-blue-600 text-blue-100 text-sm rounded-full">
                  Pool
                </span>
              )}
              {property.SolarPowerYN === 'Y' && (
                <span className="px-3 py-1 bg-yellow-700 text-yellow-100 text-sm rounded-full">
                  Solar Power
                </span>
              )}
              {property.AssociationYN === 'Y' && (
                <span className="px-3 py-1 bg-teal-700 text-teal-100 text-sm rounded-full">
                  HOA
                </span>
              )}
              {property.MlsStatus && (
                <span className="px-3 py-1 bg-slate-600 text-slate-100 text-sm rounded-full">
                  {property.MlsStatus}
                </span>
              )}
            </div>
          </div>

          {/* View Complete Details Button */}
          {!showFullDetails && (
            <div className="text-center">
              <button
                onClick={() => setShowFullDetails(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
              >
                <Eye className="w-5 h-5" />
                View Complete Details
              </button>
            </div>
          )}

          {/* COMPLETE DETAILS SECTION - Only shown when showFullDetails is true */}
          {showFullDetails && (
            <div className="space-y-8 border-t border-gray-600 pt-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white">Complete Property Details</h3>
                <p className="text-gray-400 mt-2">All available property information from MLS and public records</p>
              </div>

              {/* Complete Financial Details */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-6">Complete Financial Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Transaction Details</h5>
                    {property?.DaysOnMarket && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Days on Market:</span>
                        <span className="text-white font-medium">{property.DaysOnMarket} days</span>
                      </div>
                    )}
                    {property?.CloseDate && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Close Date:</span>
                        <span className="text-white font-medium">{formatDate(property.CloseDate)}</span>
                      </div>
                    )}
                    {property?.ListingContractDate && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Contract Date:</span>
                        <span className="text-white font-medium">{formatDate(property.ListingContractDate)}</span>
                      </div>
                    )}
                    {property?.PurchaseContractDate && (
                      <div className="flex justify-between">
                        <span className="text-slate-300">Purchase Contract:</span>
                        <span className="text-white font-medium">{formatDate(property.PurchaseContractDate)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Assessment Details</h5>
                    {(property?.PublicRecord?.ImprovementsValue) && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Improvements Value:</span>
                        <span className="text-white font-medium">{formatCurrency(property.PublicRecord.ImprovementsValue)}</span>
                      </div>
                    )}
                    {(property?.PublicRecord?.LandValue) && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Land Value:</span>
                        <span className="text-white font-medium">{formatCurrency(property.PublicRecord.LandValue)}</span>
                      </div>
                    )}
                    {(property?.PublicRecord?.AssessedYear) && (
                      <div className="flex justify-between">
                        <span className="text-slate-300">Assessment Year:</span>
                        <span className="text-white font-medium">{property.PublicRecord.AssessedYear}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Market Status</h5>
                    {property?.MlsStatus && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">MLS Status:</span>
                        <span className="text-white font-medium">{property.MlsStatus}</span>
                      </div>
                    )}
                    {property?.StandardStatus && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Standard Status:</span>
                        <span className="text-white font-medium">{property.StandardStatus}</span>
                      </div>
                    )}
                    {property?.ListingType && (
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Listing Type:</span>
                        <span className="text-white font-medium">{property.ListingType}</span>
                      </div>
                    )}
                    {property?.DistressedYN && (
                      <div className="flex justify-between">
                        <span className="text-slate-300">Distressed:</span>
                        <span className="text-white font-medium">{property.DistressedYN === 'Y' ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Property Details Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Property Information */}
                <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    Property Details & Structure
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Property Type:</span>
                      <span className="text-white font-medium">{property?.PropertySubType || property?.AssessorPropertySubType || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Architectural Style:</span>
                      <span className="text-white font-medium">{property?.ArchitecturalStyle || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Construction Materials:</span>
                      <span className="text-white font-medium">{property?.ConstructionMaterials || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Roof Type:</span>
                      <span className="text-white font-medium">{property?.Roof || property?.RoofCoverType || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Foundation Area:</span>
                      <span className="text-white font-medium">
                        {property?.PublicRecord?.FoundationArea ? `${parseInt(property.PublicRecord.FoundationArea).toLocaleString()} sq ft` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Main Level Area:</span>
                      <span className="text-white font-medium">
                        {property?.PublicRecord?.MainLevelArea ? `${parseInt(property.PublicRecord.MainLevelArea).toLocaleString()} sq ft` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Above Grade Area:</span>
                      <span className="text-white font-medium">
                        {property?.PublicRecord?.AboveGradeFinishedArea ? `${parseInt(property.PublicRecord.AboveGradeFinishedArea).toLocaleString()} sq ft` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Systems & Utilities */}
                <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Systems & Utilities
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Heating System:</span>
                      <span className="text-white font-medium">{property?.Heating || property?.HeatingType || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Cooling System:</span>
                      <span className="text-white font-medium">{property?.Cooling || property?.CoolingType || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Water Source:</span>
                      <span className="text-white font-medium">{property?.WaterSource || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Water Access:</span>
                      <span className="text-white font-medium">{property?.WaterAccess || (property?.WaterYN === 'Y' ? 'Yes' : 'N/A')}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Solar Power:</span>
                      <span className="text-white font-medium">{property?.SolarPowerYN === 'Y' ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Association:</span>
                      <span className="text-white font-medium">{property?.AssociationYN === 'Y' ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Guest House:</span>
                      <span className="text-white font-medium">{property?.GuestHouseYN === 'Y' ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Room & Space Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                    <Square className="w-5 h-5" />
                    Detailed Room Information
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Bedrooms Total:</span>
                      <span className="text-white font-medium">{property?.BedroomsTotal || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Bathrooms Full:</span>
                      <span className="text-white font-medium">{property?.BathroomsFull || property?.BathroomsFullCalculated || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Bathrooms Half:</span>
                      <span className="text-white font-medium">{property?.BathroomsHalf || property?.BathroomsHalfCalculated || '0'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Total Bathrooms:</span>
                      <span className="text-white font-medium">{property?.BathroomsDecimal || property?.BathroomsTotalInteger || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Total Rooms:</span>
                      <span className="text-white font-medium">{property?.RoomsTotal || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Fireplaces:</span>
                      <span className="text-white font-medium">{property?.FireplacesTotal || property?.FireplaceTotal || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Has Fireplace:</span>
                      <span className="text-white font-medium">{property?.FireplaceYN === 'Y' ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                    <Car className="w-5 h-5" />
                    Parking & Garage Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Has Garage:</span>
                      <span className="text-white font-medium">{property?.GarageYN === 'Y' ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Garage Spaces:</span>
                      <span className="text-white font-medium">{property?.GarageSpaces || property?.ParkingSpaces || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Garage Area:</span>
                      <span className="text-white font-medium">
                        {property?.GarageArea || property?.PublicRecord?.GarageArea ? 
                          `${parseInt(property?.GarageArea || property?.PublicRecord?.GarageArea).toLocaleString()} sq ft` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-600 pb-2">
                      <span className="text-slate-300">Car Storage Type:</span>
                      <span className="text-white font-medium">{property?.CarStorageType || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Parking Features:</span>
                      <span className="text-white font-medium">{property?.ParkingFeatures || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* School Information */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Complete School District Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3 text-sm">
                    <h5 className="text-blue-200 font-medium">Schools</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Elementary School:</span>
                        <span className="text-white font-medium">{property?.ElementarySchool || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Middle School:</span>
                        <span className="text-white font-medium">{property?.MiddleOrJuniorSchool || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">High School:</span>
                        <span className="text-white font-medium">{property?.HighSchool || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    <h5 className="text-blue-200 font-medium">School Districts</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">High School District:</span>
                        <span className="text-white font-medium">{property?.HighSchoolDistrict || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Elementary District:</span>
                        <span className="text-white font-medium">{property?.ElementarySchoolDistrict || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Middle School District:</span>
                        <span className="text-white font-medium">{property?.MiddleOrJuniorSchoolDistrict || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complete Listing Agent Information */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Complete Listing Agent & Office Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Agent Information</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Agent Name:</span>
                        <span className="text-white font-medium">{property?.ListAgentFullName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">License Number:</span>
                        <span className="text-white font-medium">{property?.ListAgentStateLicense || property?.ListingAgentStateLicense || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Phone:</span>
                        <span className="text-white font-medium">{property?.ListAgentPreferredPhone || property?.ListingAgentDirectPhone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Email:</span>
                        <span className="text-white font-medium text-xs">{property?.ListAgentEmail || property?.ListingAgentEmail || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Address:</span>
                        <span className="text-white font-medium text-xs">{property?.AgentAddress || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Office Information</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Office Name:</span>
                        <span className="text-white font-medium">{property?.ListOfficeName || property?.ListingOfficeName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Office Phone:</span>
                        <span className="text-white font-medium">{property?.ListOfficePhone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Office Email:</span>
                        <span className="text-white font-medium text-xs">{property?.ListOfficeEmail || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Office Address:</span>
                        <span className="text-white font-medium text-xs">{property?.OfficeAddress1 || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complete Property Records & Legal Information */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Complete Property Records & Legal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">MLS & Listing Info</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">MLS Number:</span>
                        <span className="text-white font-medium">{property?.MlsListingNumber || property?.MLSNumber || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Listing ID:</span>
                        <span className="text-white font-medium">{property?.ListingId || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">MLS Board:</span>
                        <span className="text-white font-medium">{property?.MlsBoard || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Property Ref ID:</span>
                        <span className="text-white font-medium">{property?.PropertyRefID || property?.PMXPropertyId || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Data Source:</span>
                        <span className="text-white font-medium">{property?.DataSource || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Legal & Tax Info</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Parcel Number:</span>
                        <span className="text-white font-medium text-xs">{property?.ParcelNumber || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">FIPS Code:</span>
                        <span className="text-white font-medium">{property?.FIPS || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Census Tract:</span>
                        <span className="text-white font-medium">{property?.PublicRecord?.CensusTractId || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Zoning:</span>
                        <span className="text-white font-medium">{property?.Zoning || property?.ZoningDescription || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Ownership Model:</span>
                        <span className="text-white font-medium">{property?.OwnershipModel || property?.PublicRecord?.LeaseholdOrFeeSimple || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Geographic Info</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Subdivision:</span>
                        <span className="text-white font-medium">{property?.SubDivisionName || property?.PublicRecord?.LegalSubdivisionName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Latitude:</span>
                        <span className="text-white font-medium">{property?.Latitude || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Longitude:</span>
                        <span className="text-white font-medium">{property?.Longitude || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Proximity Direction:</span>
                        <span className="text-white font-medium">{property?.ProximityDirection || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Distance (mi):</span>
                        <span className="text-white font-medium">{property?.Distance || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ownership & Sale History */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Ownership & Sale History
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Current Ownership</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Owner Name:</span>
                        <span className="text-white font-medium">{property?.PublicRecord?.OwnerName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Owner Occupied:</span>
                        <span className="text-white font-medium">{property?.PublicRecord?.OwnerOccupied || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Corporation Owner:</span>
                        <span className="text-white font-medium">{property?.Owner1IsCorporation ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Corporate Name:</span>
                        <span className="text-white font-medium">{property?.Owner1FullName || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Sale History</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Last Sale Date:</span>
                        <span className="text-white font-medium">
                          {property?.PublicRecord?.LastSaleDate ? formatDate(property.PublicRecord.LastSaleDate) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Recording Date:</span>
                        <span className="text-white font-medium">
                          {property?.PublicRecord?.LastSaleRecordingDate ? formatDate(property.PublicRecord.LastSaleRecordingDate) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Last Sale Price:</span>
                        <span className="text-white font-medium">
                          {property?.PublicRecord?.LastSalePrice ? `${parseInt(property.PublicRecord.LastSalePrice).toLocaleString()}` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Sale Document Type:</span>
                        <span className="text-white font-medium">{property?.PublicRecord?.SaleDocType || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Recording Doc ID:</span>
                        <span className="text-white font-medium">{property?.PublicRecord?.LastSaleRecordingDocumentId || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interior & Exterior Features - Complete */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-blue-300 mb-4">Complete Interior Features</h4>
                  <div className="space-y-4 text-sm">
                    {property?.InteriorFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Interior Features:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.InteriorFeatures}</p>
                      </div>
                    )}
                    {property?.Appliances && (
                      <div>
                        <span className="text-blue-200 font-medium">Included Appliances:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.Appliances}</p>
                      </div>
                    )}
                    {property?.FireplaceFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Fireplace Features:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.FireplaceFeatures}</p>
                      </div>
                    )}
                    {property?.Basement && (
                      <div>
                        <span className="text-blue-200 font-medium">Basement:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.Basement}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-blue-300 mb-4">Complete Exterior & Community Features</h4>
                  <div className="space-y-4 text-sm">
                    {property?.ExteriorFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Exterior Features:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.ExteriorFeatures}</p>
                      </div>
                    )}
                    {property?.PatioAndPorchFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Patio & Porch:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.PatioAndPorchFeatures}</p>
                      </div>
                    )}
                    {property?.CommunityFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Community Features:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.CommunityFeatures}</p>
                      </div>
                    )}
                    {property?.LotFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Lot Features:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.LotFeatures}</p>
                      </div>
                    )}
                    {property?.View && (
                      <div>
                        <span className="text-blue-200 font-medium">View:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.View}</p>
                      </div>
                    )}
                    {property?.WaterfrontFeatures && (
                      <div>
                        <span className="text-blue-200 font-medium">Waterfront Features:</span>
                        <p className="text-white text-xs mt-1 leading-relaxed">{property.WaterfrontFeatures}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lot & Land Information */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                  <TreePine className="w-5 h-5" />
                  Complete Lot & Land Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Lot Size & Dimensions</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Lot Size (Sq Ft):</span>
                        <span className="text-white font-medium">
                          {property?.LotSizeSquareFeet ? `${parseInt(property.LotSizeSquareFeet).toLocaleString()}` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Lot Size (Acres):</span>
                        <span className="text-white font-medium">{property?.LotSizeAcres || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Lot Dimensions:</span>
                        <span className="text-white font-medium">{property?.LotSizeDimensions || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Topography:</span>
                        <span className="text-white font-medium">{property?.Topography || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Pool & Recreation</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Pool Type:</span>
                        <span className="text-white font-medium">{property?.PoolType || 'None'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Pool Features:</span>
                        <span className="text-white font-medium">{property?.PoolFeatures || 'None'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Other Structures:</span>
                        <span className="text-white font-medium">{property?.OtherStructures || 'None'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">View Factors:</span>
                        <span className="text-white font-medium">{property?.ViewFactors || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="text-blue-200 font-medium">Location Features</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Location Factors:</span>
                        <span className="text-white font-medium">{property?.LocationFactors || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-600 pb-2">
                        <span className="text-slate-300">Carrier Route:</span>
                        <span className="text-white font-medium">{property?.CarrierRoute || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">Land Use Code:</span>
                        <span className="text-white font-medium">{property?.LandUseCode || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complete Timeline & Timestamps */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Complete Property Timeline & Important Dates
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  {property?.ListingContractDate && (
                    <div className="text-center p-3 bg-slate-600 rounded-lg">
                      <div className="text-white font-medium">Listed</div>
                      <div className="text-slate-300 mt-1">{formatDate(property.ListingContractDate)}</div>
                    </div>
                  )}
                  {property?.PurchaseContractDate && (
                    <div className="text-center p-3 bg-slate-600 rounded-lg">
                      <div className="text-white font-medium">Under Contract</div>
                      <div className="text-slate-300 mt-1">{formatDate(property.PurchaseContractDate)}</div>
                    </div>
                  )}
                  {property?.CloseDate && (
                    <div className="text-center p-3 bg-slate-600 rounded-lg">
                      <div className="text-white font-medium">Closed</div>
                      <div className="text-slate-300 mt-1">{formatDate(property.CloseDate)}</div>
                    </div>
                  )}
                  {property?.ModificationTimestamp && (
                    <div className="text-center p-3 bg-slate-600 rounded-lg">
                      <div className="text-white font-medium">Last Updated</div>
                      <div className="text-slate-300 mt-1">{formatDate(property.ModificationTimestamp)}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* All Property Highlights Tags */}
              <div className="bg-slate-700 border border-slate-600 p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-blue-300 mb-4">All Property Highlights & Features</h4>
                <div className="flex flex-wrap gap-2">
                  {/* System Tags */}
                  {property?.Heating && (
                    <span className="px-3 py-1 bg-green-700 text-green-100 text-sm rounded-full">
                      {property.Heating} Heat
                    </span>
                  )}
                  {property?.Cooling && (
                    <span className="px-3 py-1 bg-cyan-700 text-cyan-100 text-sm rounded-full">
                      {property.Cooling}
                    </span>
                  )}
                  {property?.ConstructionMaterials && (
                    <span className="px-3 py-1 bg-orange-700 text-orange-100 text-sm rounded-full">
                      {property.ConstructionMaterials}
                    </span>
                  )}
                  {property?.GuestHouseYN === 'Y' && (
                    <span className="px-3 py-1 bg-pink-700 text-pink-100 text-sm rounded-full">
                      Guest House
                    </span>
                  )}
                  {property?.DistressedYN === 'Y' && (
                    <span className="px-3 py-1 bg-red-600 text-red-100 text-sm rounded-full">
                      Distressed Sale
                    </span>
                  )}
                  {property?.FlipYN === 'Y' && (
                    <span className="px-3 py-1 bg-purple-600 text-purple-100 text-sm rounded-full">
                      Flip Property
                    </span>
                  )}
                  {property?.WaterYN === 'Y' && (
                    <span className="px-3 py-1 bg-blue-800 text-blue-100 text-sm rounded-full">
                      Water Access
                    </span>
                  )}
                  {property?.ElementarySchool && (
                    <span className="px-3 py-1 bg-emerald-700 text-emerald-100 text-sm rounded-full">
                      {property.ElementarySchool} Elementary
                    </span>
                  )}
                  {property?.HighSchool && (
                    <span className="px-3 py-1 bg-emerald-600 text-emerald-100 text-sm rounded-full">
                      {property.HighSchool} High School
                    </span>
                  )}
                  {property?.TransactionSource && (
                    <span className="px-3 py-1 bg-violet-700 text-violet-100 text-sm rounded-full">
                      {property.TransactionSource}
                    </span>
                  )}
                </div>
              </div>

              {/* Hide Details Button */}
              <div className="text-center border-t border-gray-600 pt-8">
                <button
                  onClick={() => setShowFullDetails(false)}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <EyeOff className="w-5 h-5" />
                  Hide Complete Details
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailsModal;